import omit from 'lodash/fp/omit';
import { Route, useLocation } from 'react-router';
import SuperAdminLayout from '@/components/layouts/SuperAdminLayout';
import OrganizationOverviewPage from '@/pages/superAdmin/OrganizationOverview';
import QueueDashboardPage from '@/pages/superAdmin/QueueDashboard';
import SupportImportPage from '@/pages/superAdmin/SupportImport';
import SupportImportsPage from '@/pages/superAdmin/SupportImports';
import Users from '@/pages/Users';
import NotFound from '../components/blocks/NotFound';
import CenteredLayout from '../components/layouts/CenteredLayout';
import { useUser } from '../contexts/UserSession';
import InvitationPage from '../pages/Invitation';
import LoginPage from '../pages/LoginPage';
import MSALCallback from '../pages/LoginPage/MSAL/MSALCallback';
import OIDCCallback from '../pages/LoginPage/OIDC/OIDCCallback';
import OrganizationPage from '../pages/Organization';
import ProfilePage from '../pages/ProfilePage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import SignOutPage from '../pages/SignOutPage';
import OrganizationsPage from '../pages/superAdmin/Organizations';
import RedirectTo from '../RedirectTo';
import SentryRoutes from './SentryRoutes';

const SuperAdminRoutes = () => {
    const user = useUser();
    const location = useLocation();

    if (!user) {
        // Strip the transient `isAuthenticated` flag from the captured state.
        // It is set on the destination during login to suppress the login flash,
        // but must not bleed into a subsequent logout where it would make the
        // login page render the loader instead of the sign-in form.
        const cleanFromState = omit('isAuthenticated', location.state ?? {});
        const redirectState = { from: location.pathname + location.search, fromState: cleanFromState };

        return (
            <SentryRoutes>
                <Route element={<CenteredLayout />} path="/invitation/*">
                    <Route element={<InvitationPage />} index />
                    <Route element={<NotFound homePath="/invitation/" />} path="*" />
                </Route>
                <Route element={<CenteredLayout />} path="/login/*">
                    <Route element={<LoginPage />} index />
                    <Route element={<ResetPasswordPage />} path="resetPassword" />
                    <Route element={<MSALCallback />} path="sso/msal" />
                    <Route element={<OIDCCallback />} path="sso/oidc" />
                    <Route element={<NotFound homePath="/login/" />} path="*" />
                </Route>
                <Route element={<RedirectTo state={redirectState} to="/login/" />} path="*" />
            </SentryRoutes>
        );
    }

    const redirectTo = (
        <RedirectTo to={location.state?.from || '/'} state={{ ...location.state?.fromState, isAuthenticated: true }} />
    );

    return (
        <SentryRoutes>
            <Route element={redirectTo} path="/login/*" />
            <Route element={redirectTo} path="/invitation/*" />
            <Route element={<SuperAdminLayout />} path="*">
                <Route element={<RedirectTo to="/organizations" />} index />
                <Route element={<OrganizationsPage />} path="organizations" />
                <Route element={<OrganizationOverviewPage />} path="organizations/:id" />
                <Route element={<OrganizationPage />} path="organizations/:id/settings" />
                <Route element={<SupportImportsPage />} path="organizations/:id/imports" />
                <Route element={<SupportImportPage />} path="organizations/:id/imports/:importId" />
                <Route element={<Users />} path="users" />
                <Route element={<QueueDashboardPage />} path="queues" />
                <Route element={<ProfilePage />} path="profile" />
                <Route element={<SignOutPage />} path="signout" />
                <Route element={<NotFound homePath="/" />} path="*" />
            </Route>
        </SentryRoutes>
    );
};

export default SuperAdminRoutes;
